import { LoadingOverlay, Modal, Space } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import type { Dispatch, SetStateAction } from 'react';
import { useParams } from 'react-router-dom';
import { SmallModal } from '../../../components/SmallModal';
import { Title } from '../../../components/Title';
import { FormButtons } from '../../../components/form/FormButtons';
import { mutate } from '../../../util/api/mutate';
import { useUpsertPrescriptionForm } from '../hooks/useUpsertPrescriptionForm';
import type {
	PrescriptionDto,
	UpsertPrescriptionDto,
} from '../types/PrescriptionDto';
import { UpsertPrescriptionForm } from './UpsertPrescriptionForm';

interface EditPrescriptionModalProps {
	close: () => void;
	opened: boolean;
	initialPrescription: UpsertPrescriptionDto;
	updateTableValue: Dispatch<SetStateAction<PrescriptionDto>>;
}

export function EditPrescriptionModal({
	close,
	opened,
	initialPrescription,
	updateTableValue,
}: EditPrescriptionModalProps) {
	const { organisation } = useParams();

	const [
		showSuccessModal,
		{ open: openSuccessModal, close: closeSuccessModal },
	] = useDisclosure(false);

	const { form, inputs } = useUpsertPrescriptionForm(initialPrescription);

	const editPrescriptionMutation = mutate<UpsertPrescriptionDto, PrescriptionDto>(
		`${organisation}/prescriptions/edit`,
		inputs,
		'multipart/form-data',
	);

	const resetAndClose = () => {
		form.reset();
		close();
		closeSuccessModal();
	};

	const complete = () => {
		updateTableValue((current) => ({
			...current,
			...editPrescriptionMutation.data?.value,
			...inputs,
			branchId: inputs.branchId ? Number(inputs.branchId) : current.branchId,
		}));

		form.setInitialValues(inputs);
		resetAndClose();
	};

	return (
		<Modal
			opened={opened}
			onClose={resetAndClose}
			title={<Title label={'Edit Prescription'} />}
			withCloseButton={true}
			size={'xl'}
			centered
		>
			<LoadingOverlay visible={editPrescriptionMutation.isLoading} />
			<SmallModal
				isOpen={showSuccessModal}
				close={complete}
				title={'Prescription Edited!'}
				body={
					'The prescription record has been successfully edited. Click Ok to continue...'
				}
			/>
			<Space h={6} />
			<form
				onSubmit={form.onSubmit(async () => {
					await editPrescriptionMutation.mutateAsync().then((response) => {
						if (response.isValid) {
							openSuccessModal();
						}
					});
				})}
			>
				<UpsertPrescriptionForm form={form} />
				<Space h={20} />
				<FormButtons close={resetAndClose} />
			</form>
		</Modal>
	);
}
